import styled from 'styled-components';
import _pick from 'lodash/pick';
import React, {FC} from 'react';

import {mediaBreakpointDown, mediaBreakpointUp, mediaBreakpointOnly} from '../../../../lib/styleVariables';

import {StylableProps} from '../../../../lib/styleHelpers';
import {ImageProps, Image} from '../../../common/image';
import {IconProps, Icon} from '../../../molecules/icon';
import {paletteR4, fontSizesMixinsR4, fontSizesMixinsMobileR4} from '../../../../lib/styleVariablesR4';
import {SecondaryButton} from '../../../molecules/buttonsR4';
import {hiddenTabletDownStyle, hiddenDesktopUpStyle} from '../../../../lib/responsiveClassNames';

export interface ImageTextDescButtonCssProps {
  containerCss?: string;
  contentCss?: string;
  contentWrapperCss?: string;
  titleCss?: string;
  descriptionCss?: string;
  mediaWrapperCss?: string;
}
const descButtonPropsCssKeys: (keyof ImageTextDescButtonCssProps)[] = [
  'containerCss',
  'contentCss',
  'contentWrapperCss',
  'titleCss',
  'descriptionCss',
  'mediaWrapperCss'
];

export const getCssProps = (props: any) => _pick(props, descButtonPropsCssKeys);

export interface ImageTextDescButtonProps extends ImageTextDescButtonCssProps, StylableProps {
  category?: string;
  title: React.ReactNode;
  content: React.ReactNode;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  videoSrc?: string;
  imgSrc?: ImageProps['fluid'];
  mediaType: 'image' | 'video';
  mediaPosition: 'left' | 'right';
  isImageClickable?: boolean;

  topIconName?: IconProps['name'];
  titleColor?: string;
  useSmallIcon?: boolean;
  isCenteredTitle?: boolean;
  footnote?: string;
}

const StyledContainer = styled.section<{$css?: string}>`
  width: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;

  ${mediaBreakpointDown('tablet')} {
    flex-direction: column;
  }
  ${mediaBreakpointUp('desktop')} {
    justify-content: space-between;
  }
  ${props => props.$css}
`;

interface MediaWrapperProps {
  $css?: string;
  isMediaOnLeft: boolean;
  isVideo: boolean;
  isCenteredTitle?: boolean;
}

const StyledMediaWrapper = styled.div<MediaWrapperProps>`
  width: 100%;
  max-width: 600px;
  flex: 1;
  order: ${props => (props.isMediaOnLeft ? '-1' : '1')};
  border-radius: ${props => (props.isVideo ? '0' : '16px')};
  overflow: hidden;

  ${mediaBreakpointDown('tablet')} {
    width: 100%;
    max-width: 600px;
    margin: 0 auto 16px auto;
    order: ${props => (props.isCenteredTitle ? '-1' : '0')};
  }
  ${props => props.$css}
`;

const contentWrapperCentereTitleStyle = `
  width: 100%;
  justify-content: flex-start;
`;

const StyledContentWrapper = styled.div<{$css?: string; isCenteredTitle?: boolean}>`
  display: flex;
  flex: 1;
  align-self: center;
  justify-content: center;

  ${mediaBreakpointDown('tablet')} {
    margin-bottom: 40px;
    order: ${props => (props.isCenteredTitle ? '0' : '-1')};
  }
  ${mediaBreakpointDown('mobile')} {
    ${props => props.isCenteredTitle && contentWrapperCentereTitleStyle}
  }
  ${props => props.$css}
`;

const StyledContent = styled.div<{$css?: string; isCenteredTitle?: boolean; isMediaOnLeft?: boolean}>`
  ${mediaBreakpointDown('tablet')} {
    align-items: center;
    display: flex;
    flex-direction: column;
    max-width: 600px;
    text-align: center;
  }

  ${mediaBreakpointUp('desktop')} {
    max-width: ${480}px;
    margin-left: ${props => (props.isMediaOnLeft ? 70 : 0)}px;
    margin-right: ${props => (!props.isMediaOnLeft ? 70 : 0)}px;
  }
  ${props => props.$css}
`;

const StyledEyebrow = styled.div`
  ${fontSizesMixinsR4.h6()};
  margin-bottom: 24px;
  text-transform: uppercase;
  color: ${paletteR4.black.base};
  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.h5()}
    font-size: 14px;
    line-height: 17px;
  }
`;
const centeredTitleStyle = `
  ${fontSizesMixinsR4.h2()}
  text-align: center;
  margin-bottom: 120px;
  max-width: 960px;
  margin-left: auto;
  margin-right: auto;

  ${mediaBreakpointDown('tablet')} {
    margin-bottom: 40px;
    ${fontSizesMixinsMobileR4.h3()}
  }
`;

const StyledTitle = styled.h2<{$css?: string; color?: string; centered?: boolean; isLarge: boolean}>`
  ${props => (props.isLarge ? fontSizesMixinsR4.h2() : fontSizesMixinsR4.h3())};
  margin-top: 0;
  margin-bottom: 24px;
  word-break: break-word;
  color: ${props => props.color || paletteR4.black.base};

  ${mediaBreakpointDown('tablet')} {
    ${props => (props.isLarge ? fontSizesMixinsMobileR4.h2() : fontSizesMixinsMobileR4.h3())}
  }

  ${props => (props.centered ? centeredTitleStyle : '')}

  ${props => props.$css}
`;

const StyledDescription = styled.div<{$css?: string}>`
  ${fontSizesMixinsR4.p1()};
  color: ${paletteR4.black.base};
  margin: 0;
  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.p2()}
  }
  ${props => props.$css}
`;

const StyledButtonsWrapper = styled.div<{$css?: string; isCenteredTitle?: boolean}>`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  ${mediaBreakpointDown('tablet')} {
    margin-top: ${props => (props.isCenteredTitle ? 0 : 24)}px;
    width: ${props => (props.isCenteredTitle ? '100%' : 'auto')};
  }

  ${mediaBreakpointDown('mobile')} {
    flex-direction: column;
    align-items: center;
  }

  ${mediaBreakpointOnly('tablet')} {
    justify-content: center;
  }
  ${props => props.$css}
`;

const buttonStyle = `
  ${mediaBreakpointUp('tablet')} {
    &:not(:last-child) {
      margin-right: 16px;
    }
  }

  ${mediaBreakpointDown('mobile')} {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  ${buttonStyle}
  ${mediaBreakpointDown('mobile')} {
    width: 100%;
  }
`;

const mediaStyle = `
  width: 100%;
  vertical-align: middle;
  ${mediaBreakpointDown('tablet')} {
    object-position: center left;
  }
`;

const StyledClickableImage = styled.a`
  display: block;
`;

const StyledImage = styled(Image)<{isMediaOnLeft: boolean}>`
  object-fit: contain;

  ${mediaBreakpointUp('desktop')} {
    object-position: center ${props => (props.isMediaOnLeft ? 'left' : 'right')};
  }

  > img,
  > picture img {
    object-fit: contain !important;
  }

  ${mediaStyle}
`;

const StyledVideo = styled.video<{isMediaOnLeft: boolean}>`
  align-self: center;
  object-fit: contain;

  ${mediaBreakpointUp('desktop')} {
    object-position: center ${props => (props.isMediaOnLeft ? 'left' : 'right')};
  }

  ${mediaStyle}
`;

const StyledTopIcon = styled(Icon)<{useSmallIcon?: boolean}>`
  margin-bottom: ${props => (props.useSmallIcon ? 24 : 32)}px;

  ${mediaBreakpointDown('tablet')} {
    margin-bottom: ${props => (props.useSmallIcon ? 16 : 8)}px;
  }
`;

const StyledCenteredTitleWrapper = styled.div`
  width: 100%;
`;

const StyledFootnote = styled.div`
  margin-top: 80px;

  ${mediaBreakpointDown('tablet')} {
    margin-top: 40px;
  }
`;

export const ImageTextDescButton: FC<ImageTextDescButtonProps> = props => {
  const {
    category,
    title,
    content,
    primaryButtonText,
    primaryButtonLink,
    secondaryButtonText,
    secondaryButtonLink,
    videoSrc,
    imgSrc,
    mediaType,
    mediaPosition,
    footnote,
    containerCss,
    contentCss,
    contentWrapperCss,
    titleCss,
    descriptionCss,
    mediaWrapperCss,
    isImageClickable
  } = props;
  const isMediaOnLeft = mediaPosition === 'left';
  const hasPrimaryButton = primaryButtonText && primaryButtonLink;
  const hasSecondaryButton = secondaryButtonLink && secondaryButtonText;
  const hasAtLeastOneButton = hasPrimaryButton || hasSecondaryButton;
  const hasVideo = mediaType === 'video';
  const hasImage = mediaType === 'image';
  const buttonComponents = (
    <>
      {hasPrimaryButton && (
        <StyledSecondaryButton transparentBackground href={primaryButtonLink}>
          {primaryButtonText}
        </StyledSecondaryButton>
      )}

      {hasSecondaryButton && (
        <StyledSecondaryButton transparentBackground href={secondaryButtonLink}>
          {secondaryButtonText}
        </StyledSecondaryButton>
      )}
    </>
  );

  const isLargeTitle = !props.category && !props.topIconName;

  const titleComponent = (
    <StyledTitle
      color={props.titleColor}
      $css={titleCss}
      centered={props.isCenteredTitle}
      isLarge={isLargeTitle}
    >
      {title}
    </StyledTitle>
  );

  const descButtonComponent = (
    <StyledContainer className={props.className} $css={containerCss}>
      <StyledContentWrapper $css={contentWrapperCss} isCenteredTitle={props.isCenteredTitle}>
        <StyledContent
          $css={contentCss}
          isCenteredTitle={props.isCenteredTitle}
          isMediaOnLeft={isMediaOnLeft}
        >
          {props.topIconName && (
            <StyledTopIcon
              useR4
              useSmallIcon={props.useSmallIcon}
              size={props.useSmallIcon ? 40 : 80}
              name={props.topIconName}
              color={props.titleColor}
            />
          )}
          {category && <StyledEyebrow>{category}</StyledEyebrow>}
          {!props.isCenteredTitle && titleComponent}
          <StyledDescription $css={descriptionCss}>{content}</StyledDescription>
          {hasAtLeastOneButton && (
            <StyledButtonsWrapper $css={hiddenTabletDownStyle}>{buttonComponents}</StyledButtonsWrapper>
          )}
          {footnote && <StyledFootnote>{footnote}</StyledFootnote>}
        </StyledContent>
      </StyledContentWrapper>
      <StyledMediaWrapper
        $css={mediaWrapperCss}
        isVideo={hasVideo}
        isMediaOnLeft={isMediaOnLeft}
        isCenteredTitle={props.isCenteredTitle}
      >
        {hasImage &&
          (isImageClickable ? (
            <StyledClickableImage href={primaryButtonLink || secondaryButtonLink}>
              <StyledImage isMediaOnLeft={isMediaOnLeft} fluid={imgSrc} />
            </StyledClickableImage>
          ) : (
            <StyledImage isMediaOnLeft={isMediaOnLeft} fluid={imgSrc} />
          ))}
        {hasVideo && (
          <StyledVideo isMediaOnLeft={isMediaOnLeft} autoPlay loop muted playsInline>
            <source src={`${videoSrc}#t=0.1`} />
          </StyledVideo>
        )}
      </StyledMediaWrapper>
      {hasAtLeastOneButton && (
        <StyledButtonsWrapper isCenteredTitle={props.isCenteredTitle} $css={hiddenDesktopUpStyle}>
          {buttonComponents}
        </StyledButtonsWrapper>
      )}
    </StyledContainer>
  );

  if (props.isCenteredTitle)
    return (
      <StyledCenteredTitleWrapper>
        {titleComponent}
        {descButtonComponent}
      </StyledCenteredTitleWrapper>
    );

  return descButtonComponent;
};
